import { ChevronRight, SearchX } from "lucide-react";
import { useMemo, useState } from "react";

import ScorecardEmptyState from "../Rows/ScorecardEmptyState";
import ScorecardSectionBlockHeader from "../Detail/ScorecardSectionBlockHeader";
import type { ScorecardCriterionAddScope } from "../../types";
import { CRITERION_CATALOG, type CriterionCatalogItem } from "../../criterionCatalog";
import { SCORECARD_CARD } from "../../constants";

import { ScorecardPrimitiveType } from "@/queries/smart-tables/scorecard";
import SearchBar from "@/components/ui/SearchBar";
import { cn } from "@/lib/utils";

interface ScorecardAddCriterionMenuProps {
  scope?: ScorecardCriterionAddScope;
  onSelect: (item: CriterionCatalogItem) => void;
  onClose: () => void;
}

const matchesQuery = (item: CriterionCatalogItem, normalizedQuery: string): boolean =>
  item.label.toLowerCase().includes(normalizedQuery) ||
  item.description.toLowerCase().includes(normalizedQuery);

const getTitle = (scope: ScorecardCriterionAddScope): string => {
  if (scope === "scorecard-wide") {
    return "Add column evaluator";
  }
  return "Add evaluator";
};

const getDescription = (scope: ScorecardCriterionAddScope): string => {
  if (scope === "scorecard-wide") {
    return "Create one scorecard-wide evaluator from existing values.";
  }
  return "Choose how each row should be judged. Start simple — you can add more later.";
};

const QUALITY_CRITERION_ITEMS =
  CRITERION_CATALOG.find(category => category.id === "quality")?.items ?? [];
const ROW_EVALUATOR_AGENT_TYPES = new Set<string>([ScorecardPrimitiveType.TRAJECTORY]);
const AGENT_BEHAVIOR_CRITERION_ITEMS =
  CRITERION_CATALOG.find(category => category.id === "agent-behavior")?.items ?? [];
const ROW_EVALUATOR_ITEMS = [
  ...QUALITY_CRITERION_ITEMS,
  ...AGENT_BEHAVIOR_CRITERION_ITEMS.filter(
    item => item.implemented && ROW_EVALUATOR_AGENT_TYPES.has(String(item.primitiveType)),
  ),
];

const SEARCH_PLACEHOLDER_PHRASES = [
  "Search evaluators…",
  "AI Judge",
  "Code Check",
  "Compare Values",
  "Contains Text",
  "Regex Match",
  "Valid Format",
  "Trajectory Check",
];

const OPTION_HELP_TEXT: Partial<Record<string, string>> = {
  [ScorecardPrimitiveType.COLUMN_AGGREGATE]: "Best for scorecard-wide values from a column",
  [ScorecardPrimitiveType.LLM_ASSERTION]: "Best for subjective quality and rubric-based grading",
  [ScorecardPrimitiveType.CODE_EXECUTION]: "Best for deterministic checks with custom logic",
  [ScorecardPrimitiveType.COMPARE]:
    "Best for expected columns, numeric thresholds, and JSON comparisons",
  [ScorecardPrimitiveType.CONTAINS]: "Best for required words, phrases, or facts",
  [ScorecardPrimitiveType.REGEX]: "Best for exact patterns and structured strings",
  [ScorecardPrimitiveType.ASSERT_VALID]: "Best for JSON, XML, or schema-like outputs",
  [ScorecardPrimitiveType.COUNT]: "Best for length and volume bounds on text outputs",
  [ScorecardPrimitiveType.TRAJECTORY]: "Best for required tool sequences in agent traces",
};

interface CriterionOptionButtonProps {
  item: CriterionCatalogItem;
  onSelect: (item: CriterionCatalogItem) => void;
  variant?: "featured" | "compact";
}

const CriterionOptionButton = ({
  item,
  onSelect,
  variant = "compact",
}: CriterionOptionButtonProps) => {
  const Icon = item.icon;
  const isFeatured = variant === "featured";
  const helpText = OPTION_HELP_TEXT[String(item.primitiveType)];

  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className={cn(
        SCORECARD_CARD,
        "group relative flex w-full text-left transition-all hover:-translate-y-0.5 hover:border-foreground/20 hover:bg-muted/15 hover:shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        isFeatured ? "min-h-[132px] flex-col justify-between p-4" : "min-h-[118px] flex-col p-3",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div
          className={cn(
            "flex shrink-0 items-center justify-center rounded-xl bg-muted/60 text-foreground transition-colors group-hover:bg-muted",
            isFeatured ? "h-10 w-10" : "h-8 w-8",
          )}
        >
          <Icon className={cn(isFeatured ? "h-[18px] w-[18px]" : "h-4 w-4")} />
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5 group-hover:text-muted-foreground" />
      </div>

      <div className="min-w-0 space-y-1.5 pt-3">
        <div
          className={cn(
            "font-medium leading-5 text-foreground",
            isFeatured ? "text-base" : "text-sm",
          )}
        >
          {item.label}
        </div>
        <div
          className={cn(
            "break-words text-xs leading-5 text-muted-foreground",
            isFeatured ? "line-clamp-2" : "line-clamp-3",
          )}
        >
          {helpText ?? item.description}
        </div>
      </div>
    </button>
  );
};

const ScorecardAddCriterionMenu = ({ scope = "all", onSelect }: ScorecardAddCriterionMenuProps) => {
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLowerCase();
  const title = getTitle(scope);
  const description = getDescription(scope);

  const filteredCatalog = useMemo(() => {
    const catalogItems =
      scope === "scorecard-wide"
        ? QUALITY_CRITERION_ITEMS.filter(
            item => item.primitiveType === ScorecardPrimitiveType.COLUMN_AGGREGATE,
          )
        : ROW_EVALUATOR_ITEMS;
    if (!normalizedQuery) {
      return catalogItems;
    }
    return catalogItems.filter(item => matchesQuery(item, normalizedQuery));
  }, [normalizedQuery, scope]);

  const featuredCriterion =
    filteredCatalog.find(item => item.primitiveType === ScorecardPrimitiveType.LLM_ASSERTION) ??
    filteredCatalog[0];
  const compactCriteria = filteredCatalog.filter(item => item !== featuredCriterion);
  const hasResults = filteredCatalog.length > 0;

  return (
    <div className="flex min-h-full flex-col gap-4">
      <div className="rounded-xl border border-border/70 bg-gradient-to-br from-muted/20 via-background to-background p-3">
        <div className="min-w-0 space-y-1">
          <h3 className="text-sm font-semibold leading-6 text-foreground">{title}</h3>
          <p className="text-xs leading-5 text-muted-foreground">{description}</p>
        </div>
      </div>

      <SearchBar
        value={query}
        onChange={setQuery}
        placeholderPhrasesArray={SEARCH_PLACEHOLDER_PHRASES}
        size="sm"
        className="w-full"
        inputClassName="rounded-lg"
      />

      <div className="flex-1 space-y-5 pb-1">
        {scope === "all" && filteredCatalog.length > 0 ? (
          <ScorecardSectionBlockHeader
            label="Evaluators"
            description="Define the checks that score each row."
            headerClassName="px-0"
          />
        ) : null}

        {featuredCriterion ? (
          <section className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <CriterionOptionButton
                  item={featuredCriterion}
                  onSelect={onSelect}
                  variant="featured"
                />
              </div>
              {compactCriteria.map(item => (
                <CriterionOptionButton key={item.primitiveType} item={item} onSelect={onSelect} />
              ))}
            </div>
          </section>
        ) : null}

        {!hasResults ? (
          <ScorecardEmptyState
            className="flex flex-col items-center gap-2 px-4 py-10"
            icon={<SearchX className="h-5 w-5 text-muted-foreground" />}
            title="No matching evaluators"
            description="Try a different search term."
          />
        ) : null}
      </div>
    </div>
  );
};

export default ScorecardAddCriterionMenu;
